import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import PropTypes from 'prop-types';
import './ExpandedChartModal.css';

// Vehicle type colors matching ChartsPopup
const vehicleTypeColors = {
  totale: '#16A34A',  // Green
  car: '#2563EB',     // Blue
  truck: '#EA580C',   // Orange
  brt: '#9333EA',     // Purple
};

const ExpandedChartModal = ({
  isOpen,
  onClose,
  chartData,
  chartConfig,
  title,
  footer,
  showTimeSelector,
  onTimeChange,
  currentTimeInterval
}) => {
  const [selectedTime, setSelectedTime] = useState(currentTimeInterval || '10min');

  if (!isOpen) return null;

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setSelectedTime(newTime);
    if (onTimeChange) {
      onTimeChange(newTime);
    }
  };

  // Detect available vehicle types from data
  const getAvailableVehicleTypes = () => {
    if (!chartData || chartData.length === 0) return [];
    const firstPoint = chartData[0];
    const types = [];
    if (typeof firstPoint.totale !== 'undefined') types.push({ key: 'totale', name: 'Totale', color: vehicleTypeColors.totale });
    if (typeof firstPoint.car !== 'undefined') types.push({ key: 'car', name: 'Car', color: vehicleTypeColors.car });
    if (typeof firstPoint.truck !== 'undefined') types.push({ key: 'truck', name: 'Truck', color: vehicleTypeColors.truck });
    if (typeof firstPoint.brt !== 'undefined') types.push({ key: 'brt', name: 'BRT', color: vehicleTypeColors.brt });
    return types;
  };

  const availableTypes = getAvailableVehicleTypes();
  const hasMultipleTypes = availableTypes.length > 0;

  return (
    <div className="expanded-chart-overlay" onClick={onClose}>
      <div className="expanded-chart-modal" onClick={(e) => e.stopPropagation()}>
        <div className="expanded-chart-header">
          <div className="expanded-chart-title">
            <h2>{title}</h2>
            {showTimeSelector && (
              <select
                value={selectedTime}
                onChange={handleTimeChange}
                className="expanded-time-selector"
              >
                <option value="10min">10min</option>
                <option value="30min">30min</option>
                <option value="1h">1h</option>
              </select>
            )}
          </div>
          <button className="expanded-chart-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="expanded-chart-content">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: hasMultipleTypes ? 30 : 10 }}>
              <defs>
                <linearGradient id={`expanded-${chartConfig.gradientId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartConfig.color} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={chartConfig.color} stopOpacity={0.1}/>
                </linearGradient>
                {/* Gradients for each vehicle type */}
                {availableTypes.map(vt => (
                  <linearGradient key={`gradient-${vt.key}`} id={`expanded-color-${vt.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={vt.color} stopOpacity={0.1}/>
                    <stop offset="95%" stopColor={vt.color} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="#e5e5e5" />
              <XAxis
                dataKey="time"
                stroke="#666"
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis
                stroke="#666"
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }}
                formatter={(value, name) => [Number(value).toFixed(2), name]}
              />
              {/* Render all available vehicle types with legend */}
              {/* Keyed array, not a Fragment: recharts 2 cannot see children of a React 19 Fragment. */}
              {hasMultipleTypes ? [
                ...availableTypes.map(vt => (
                  <Area
                    key={vt.key}
                    type="stepAfter"
                    dataKey={vt.key}
                    name={vt.name}
                    stroke={vt.color}
                    strokeWidth={2}
                    fillOpacity={0}
                  />
                )),
                <Legend
                  key="legend"
                  verticalAlign="bottom"
                  height={36}
                  iconType="plainline"
                  wrapperStyle={{ bottom: 0, fontSize: '12px', fontWeight: 600 }}
                />
              ] : (
                <Area
                  type="stepAfter"
                  dataKey="value"
                  stroke={chartConfig.color}
                  strokeWidth={3}
                  fillOpacity={1}
                  fill={`url(#expanded-${chartConfig.gradientId})`}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {footer && (
          <div className="expanded-chart-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

ExpandedChartModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chartData: PropTypes.array.isRequired,
  chartConfig: PropTypes.shape({
    color: PropTypes.string.isRequired,
    gradientId: PropTypes.string.isRequired
  }).isRequired,
  title: PropTypes.string.isRequired,
  footer: PropTypes.node,
  showTimeSelector: PropTypes.bool,
  onTimeChange: PropTypes.func,
  currentTimeInterval: PropTypes.string
};

export default ExpandedChartModal;